import { memo, useMemo, useState } from 'react';
import { Tree, Tag, Tooltip } from 'antd';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RechartsTooltip } from 'recharts';
import { useTranslation } from 'react-i18next';

/**
 * LocationTreeWithChart exibe:
 *  - À esquerda (50%): o PieChart de tasks por location
 *  - À direita (50%): a árvore de locais compacta
 */
function LocationTreeWithChart({ tasksByLocation, handleAnswersSearchLocation }) {
    const { t } = useTranslation();
    // Estados de seleção e hover para o gráfico
    const [activeSlices, setActiveSlices] = useState([]);
    const [hoveredSlice, setHoveredSlice] = useState(null);
    const [hasAnimated, setHasAnimated] = useState(false);

    // Mapeia caminho -> ID original
    const idMap = useMemo(
        () => tasksByLocation.reduce((map, { locationName, locationId }) => {
            map[locationName] = locationId;
            return map;
        }, {}),
        [tasksByLocation]
    );

    // Soma recursiva: próprio + filhos
    const aggregatedCounts = useMemo(() => {
        const map = {};
        tasksByLocation.forEach(({ locationName, totalTasks }) => {
            let prefix = '';
            locationName.split(' > ').forEach(part => {
                prefix = prefix ? `${prefix} > ${part}` : part;
                map[prefix] = (map[prefix] || 0) + totalTasks;
            });
        });
        return map;
    }, [tasksByLocation]);

    // Tarefas apenas no nível exato
    const ownCounts = useMemo(
        () => tasksByLocation.reduce((map, { locationName, totalTasks }) => {
            map[locationName] = (map[locationName] || 0) + totalTasks;
            return map;
        }, {}),
        [tasksByLocation]
    );

    // Monta árvore com tags, tooltips, desabilitação de nós e estilo compacto
    const treeData = useMemo(() => {
        const nodeMap = Object.entries(aggregatedCounts).reduce((acc, [fullKey, total]) => {
            const label = fullKey.split(' > ').pop();
            const own = ownCounts[fullKey] || 0;
            const hasId = idMap[fullKey] != null;
            acc[fullKey] = {
                key: fullKey,
                title: (
                    <span style={{ fontSize: 12, lineHeight: '15px' }}>
                        <Tooltip title="Tarefas somente neste nível">
                            <Tag color={own > 0 ? '#4A90E2' : undefined} style={{ marginRight: 4, padding: "4px 6px", lineHeight: "10px" }}>{own}</Tag>
                        </Tooltip>
                        <strong>{label} {total != own ? <>- (Total: {total})</> : ""}</strong>
                    </span>
                ),
                children: [],
                disabled: !hasId,
                style: { padding: '0px' }
            };
            return acc;
        }, {});

        Object.keys(nodeMap).forEach(fullKey => {
            const idx = fullKey.lastIndexOf(' > ');
            if (idx !== -1) {
                const parentKey = fullKey.slice(0, idx);
                nodeMap[parentKey].children.push(nodeMap[fullKey]);
            }
        });

        return Object.values(nodeMap).filter(node => !node.key.includes(' > '));
    }, [aggregatedCounts, ownCounts, idMap]);

    // Prepara dados do PieChart (locations com respostas diretas)
    const pieData = useMemo(() => {
        const filtered = tasksByLocation.filter(({ totalTasks }) => totalTasks > 0);
        return filtered.map(({ locationName, totalTasks, locationId }) => ({
            name: locationName.split(' > ').pop(),
            value: totalTasks,
            id: locationId,
        }));
    }, [tasksByLocation]);

    // Calcula o total para porcentagem correta
    const totalTasksInChart = useMemo(() => {
        return pieData.reduce((sum, { value }) => sum + value, 0);
    }, [pieData]);

    // Função para calcular porcentagem com arredondamento que soma exatamente 100%
    const calculatePercentages = useMemo(() => {
        if (totalTasksInChart === 0 || pieData.length === 0) return new Map();
        
        const percentages = new Map();
        const items = [];
        let sumRounded = 0;
        
        // Calcula porcentagens e arredonda
        pieData.forEach((item, index) => {
            const percent = (item.value / totalTasksInChart) * 100;
            const roundedPercent = Math.round(percent * 100) / 100; // Arredonda para 2 casas decimais
            items.push({ index, percent: roundedPercent, originalPercent: percent });
            sumRounded += roundedPercent;
        });
        
        // Ajusta diferença para garantir soma = 100%
        const diff = 100 - sumRounded;
        if (Math.abs(diff) >= 0.005) { // Se diferença >= 0.005%
            // Ordena por maior diferença entre original e arredondado
            items.sort((a, b) => Math.abs(b.originalPercent - b.percent) - Math.abs(a.originalPercent - a.percent));
            // Ajusta o primeiro item (com maior diferença) para compensar
            const adjustValue = diff > 0 ? 0.01 : -0.01;
            items[0].percent = Math.round((items[0].percent + adjustValue) * 100) / 100;
        }
        
        // Cria mapa com índices
        items.forEach(({ index, percent }) => {
            percentages.set(index, Math.max(0, percent)); // Garante que não seja negativo
        });
        
        return percentages;
    }, [pieData, totalTasksInChart]);

    // Paleta de cores do projeto (mesmas cores dos outros gráficos - 100 cores fixas)
    const COLORS = [
        '#1B3A7C', '#4A90E2', '#F5A623', '#D622A9', '#BD10E0', '#9013FE', '#50E3C2', '#7ED321', '#B8E986', '#F8E71C',
        '#F45B69', '#417505', '#FF7F50', '#7F8C8D', '#3E4A59', '#FF5733', '#C70039', '#900C3F', '#581845', '#FFC300',
        '#DAF7A6', '#1ABC9C', '#2ECC71', '#3498DB', '#9B59B6', '#34495E', '#16A085', '#27AE60', '#2980B9', '#8E44AD',
        '#2C3E50', '#F1C40F', '#E67E22', '#E74C3C', '#ECF0F1', '#95A5A6', '#F39C12', '#D35400', '#C0392B', '#BDC3C7',
        '#FF9999', '#FF6666', '#FF3333', '#FF0000', '#CC0000', '#990000', '#660000', '#330000', '#FFCC99', '#FF9966',
        '#FF6633', '#CC3300', '#993300', '#663300', '#FFCC00', '#FF9900', '#FF6600', '#CC6600', '#FFFF99', '#FFFF66',
        '#FFFF33', '#FFFF00', '#CCCC00', '#999900', '#666600', '#333300', '#99FF99', '#66FF66', '#33FF33', '#00FF00',
        '#00CC00', '#009900', '#006600', '#003300', '#99FFFF', '#66FFFF', '#33FFFF', '#00FFFF', '#00CCCC', '#009999',
        '#006666', '#003333', '#9999FF', '#6666FF', '#3333FF', '#0000FF', '#0000CC', '#000099', '#000066', '#000033',
        '#E6B0AA', '#D7BDE2', '#A9CCE3', '#A3E4D7', '#F9E79F', '#FAD7A0', '#F5B7B1', '#AED6F1', '#A2D9CE', '#F8C471'
    ];

    // Alterna seleção
    const toggleSelection = (id) => {
        setActiveSlices(prev => {
            const isActive = prev.includes(id);
            const newArr = isActive ? prev.filter(i => i !== id) : [...prev, id];
            handleAnswersSearchLocation(id, 'combo', true);
            return newArr;
        });
    };

    // Handlers de clique/hover no Pie
    const onPieClick = (_, index) => pieData[index] && toggleSelection(pieData[index].id);
    const onPieMouseEnter = (data, index, e) => {
        if (data && data.payload) {
            const id = data.payload.id;
            if (id !== hoveredSlice) {
                setHoveredSlice(id);
            }
        }
    };
    const onPieMouseLeave = () => setHoveredSlice(null);

    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            {/* Gráfico de donut */}
            <div style={{ width: '50%', height: tasksByLocation && tasksByLocation.length > 0 ? 250 : 0, maxHeight: 250 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={55}
                            outerRadius={85}
                            minAngle={5}
                            paddingAngle={2}
                            isAnimationActive={!hasAnimated}
                            animationBegin={0}
                            animationDuration={800}
                            animationEasing="ease-out"
                            onAnimationEnd={() => setHasAnimated(true)}
                            onClick={onPieClick}
                            onMouseEnter={onPieMouseEnter}
                            onMouseLeave={onPieMouseLeave}
                            cursor="pointer"
                            disabled={false}
                            label={({ payload, cx, cy, midAngle, outerRadius, index }) => {
                                if (!payload) return null;
                                const { value } = payload;
                                // Usa porcentagem ajustada que garante soma = 100%
                                const percent = calculatePercentages.get(index) ?? (totalTasksInChart > 0 ? (value / totalTasksInChart) * 100 : 0);
                                const RADIAN = Math.PI / 180;
                                const radius = outerRadius + 30;
                                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                const y = cy + radius * Math.sin(-midAngle * RADIAN);
                                return (
                                    <text
                                        x={x}
                                        y={y}
                                        fill="#262626"
                                        textAnchor={x > cx ? "start" : "end"}
                                        dominantBaseline="central"
                                        fontSize="11px"
                                        fontWeight="600"
                                    >
                                        <tspan>{`${value} (${percent.toFixed(2)}%)`}</tspan>
                                    </text>
                                );
                            }}
                        >
                            {pieData.map((entry, idx) => {
                                const opacity = hoveredSlice != null
                                    ? (entry.id === hoveredSlice ? 1 : 0.3)
                                    : (activeSlices.length === 0 || activeSlices.includes(entry.id) ? 1 : 0.3);
                                const isHovered = hoveredSlice === entry.id;
                                const isActive = activeSlices.length === 0 || activeSlices.includes(entry.id);
                                return (
                                    <Cell
                                        key={entry.id}
                                        fill={COLORS[idx % COLORS.length]}
                                        opacity={opacity}
                                        stroke={isHovered || isActive ? '#fff' : 'transparent'}
                                        strokeWidth={isHovered || isActive ? 3 : 0}
                                    />
                                );
                            })}
                        </Pie>
                        <RechartsTooltip 
                            content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                    const { name, value } = payload[0].payload;
                                    // Encontra o índice do item no pieData
                                    const itemIndex = pieData.findIndex(item => item.value === value && item.name === name);
                                    // Usa porcentagem ajustada que garante soma = 100%
                                    const percent = itemIndex >= 0 && calculatePercentages.has(itemIndex) 
                                        ? calculatePercentages.get(itemIndex) 
                                        : (totalTasksInChart > 0 ? (value / totalTasksInChart) * 100 : 0);
                                    return (
                                        <div style={{
                                            backgroundColor: '#fff',
                                            border: '1px solid #e8e8e8',
                                            borderRadius: '6px',
                                            padding: '12px 16px',
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                            fontSize: '13px',
                                        }}>
                                            <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#262626', fontSize: '14px' }}>{name}</p>
                                            <p style={{ margin: '0 0 4px 0', fontWeight: '700', color: '#262626', fontSize: '24px', lineHeight: '1.2' }}>{value}</p>
                                            <p style={{ margin: 0, color: '#595959' }}>{t('Dashboard.Tooltip.Percentage')}: <strong style={{ color: '#4085c6' }}>{percent.toFixed(2)}%</strong></p>
                                        </div>
                                    );
                                }
                                return null;
                            }} 
                        />
                    </PieChart>
                </ResponsiveContainer>
            </div>

            {/* Árvore compacta */}
            <div className="ant-loc" style={{ width: '50%', height: "100%", maxHeight: 250, overflowY: 'auto' }}>
                <Tree
                    treeData={treeData}
                    indent={8}
                    virtual                  // habilita virtualização
                    height={250}             // altura total do container
                    itemHeight={20}          // altura de cada nó
                    defaultExpandAll
                    showIcon={false}         // esconde ícones padrões
                    showLine={false}         // esconde linhas de conexão
                    onSelect={([key]) => {
                        const id = idMap[key];
                        if (id != null) handleAnswersSearchLocation(id, 'combo', true);
                    }}
                />
            </div>
        </div >
    );
}

export default memo(LocationTreeWithChart);
